// ============================================================================
// Modelo de fatiga / carga sistémica
// ----------------------------------------------------------------------------
// La fatiga de un día = Σ (series × factor de fatiga del ejercicio).
// El factor por ejercicio puede setearlo el entrenador (1-5) o derivarse de la
// categoría. Basado en evidencia: los compuestos (sentadilla, peso muerto)
// cuestan mucho más en recuperación que los aislamientos.
//
// Los umbrales y presupuestos son heurísticos y fáciles de ajustar.
// ============================================================================

import type { Day, Discipline, Exercise, ExerciseCategory, TrainingBlock, TrainingLevel } from '../types';

/** Factor de fatiga por defecto según la categoría del ejercicio (1-5). */
const CATEGORY_FATIGUE: Record<ExerciseCategory, number> = {
  compound: 4,
  basic: 3,
  cardio: 2,
  isolation: 1,
};

/** Factor de fatiga efectivo de un ejercicio (override del entrenador o categoría). */
export function exerciseFatigue(exercise?: Pick<Exercise, 'category' | 'fatigueRating'>): number {
  if (!exercise) return 2;
  if (exercise.fatigueRating != null) return exercise.fatigueRating;
  return CATEGORY_FATIGUE[exercise.category] ?? 2;
}

/** Carga de un ítem = series (mín 1) × factor de fatiga. */
function itemLoad(sets: number | undefined, exercise?: Pick<Exercise, 'category' | 'fatigueRating'>): number {
  const s = sets && sets > 0 ? sets : 1;
  return s * exerciseFatigue(exercise);
}

/** Suma recursiva de la carga de los bloques (incluye anidados). */
function blocksLoad(blocks: TrainingBlock[]): number {
  let total = 0;
  for (const b of blocks) {
    for (const it of b.items ?? []) total += itemLoad(it.sets, it.exercise);
    if (b.children) total += blocksLoad(b.children);
  }
  return total;
}

/** Score de fatiga de un día. */
export function dayFatigue(day: Day): number {
  return Math.round(blocksLoad(day.blocks ?? []));
}

// ── Niveles de fatiga (clasificación visual del score) ──────────────────────

export type FatigueKey = 'light' | 'moderate' | 'high' | 'very_high';

export interface FatigueLevel {
  key: FatigueKey;
  label: string;
  /** Clases Tailwind (texto/borde/fondo) para el badge. */
  classes: string;
}

const LEVELS: { max: number; level: FatigueLevel }[] = [
  { max: 25, level: { key: 'light', label: 'Suave', classes: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' } },
  { max: 45, level: { key: 'moderate', label: 'Moderado', classes: 'text-primary-400 border-primary-500/30 bg-primary-500/10' } },
  { max: 70, level: { key: 'high', label: 'Alto', classes: 'text-accent-400 border-accent-500/30 bg-accent-500/10' } },
  { max: Infinity, level: { key: 'very_high', label: 'Muy alto', classes: 'text-red-400 border-red-500/30 bg-red-500/10' } },
];

export function fatigueLevel(score: number): FatigueLevel {
  return (LEVELS.find((l) => score <= l.max) ?? LEVELS[LEVELS.length - 1]).level;
}

// ── Matching con el nivel del atleta ────────────────────────────────────────

/** Presupuesto de fatiga por sesión (score de día "cómodo") según nivel. */
const LEVEL_BUDGET: Record<TrainingLevel, number> = {
  beginner: 30,
  intermediate: 55,
  advanced: 80,
};

/**
 * Ajuste del presupuesto según la disciplina (heurístico): algunas toleran
 * naturalmente más carga por sesión. La fuerza pesada y el alto volumen
 * funcional esperan días más exigentes; el resto queda en 1.0 (sin ajuste).
 */
const DISCIPLINE_BUDGET_FACTOR: Partial<Record<Discipline, number>> = {
  powerlifting: 1.35,
  weightlifting: 1.3,
  crossfit: 1.25,
  hybrid: 1.2,
};

export const LEVEL_LABELS: Record<TrainingLevel, string> = {
  beginner: 'Principiante',
  intermediate: 'Intermedio',
  advanced: 'Avanzado',
};

export type MatchKey = 'low' | 'ok' | 'high';

export interface MatchResult {
  key: MatchKey;
  label: string;
  classes: string;
}

/**
 * Compara la carga de un día con el presupuesto del nivel del atleta.
 * - low:  muy por debajo (podría hacer más)
 * - ok:   dentro del rango adecuado
 * - high: excede su capacidad (riesgo de sobrecarga)
 */
export function matchForLevel(
  score: number,
  level: TrainingLevel,
  discipline?: Discipline | null
): MatchResult {
  const factor = (discipline && DISCIPLINE_BUDGET_FACTOR[discipline]) || 1;
  const budget = LEVEL_BUDGET[level] * factor;
  if (score > budget) {
    return { key: 'high', label: `Exigente para ${LEVEL_LABELS[level].toLowerCase()}`, classes: 'text-red-400 border-red-500/30 bg-red-500/10' };
  }
  if (score < budget * 0.5) {
    return { key: 'low', label: `Liviano para ${LEVEL_LABELS[level].toLowerCase()}`, classes: 'text-sky-400 border-sky-500/30 bg-sky-500/10' };
  }
  return { key: 'ok', label: `Adecuado para ${LEVEL_LABELS[level].toLowerCase()}`, classes: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' };
}
